import { useLocation } from 'react-router'
import { useI18n } from './i18n'
import type { Locale } from './i18n'

export const PUBLIC_LOCALES: Locale[] = ['pl', 'en']

export function isLocale(value: string | undefined): value is Locale {
  return value === 'pl' || value === 'en'
}

export function localeFromPath(pathname: string): Locale | null {
  const first = pathname.split('/').filter(Boolean)[0]
  return isLocale(first) ? first : null
}

export function detectPublicLocale(): Locale {
  try {
    const stored = localStorage.getItem('edimi.locale') ?? undefined
    if (isLocale(stored)) return stored
  } catch {
    /* ignore blocked storage */
  }
  try {
    const language = navigator.language.toLowerCase()
    if (language.startsWith('en')) return 'en'
  } catch {
    /* ignore non-browser env */
  }
  return 'pl'
}

export function publicPath(locale: Locale, target = ''): string {
  if (!target || target === '/') return `/${locale}`
  if (target.startsWith('#')) return `/${locale}${target}`
  if (target.startsWith('/#')) return `/${locale}${target.slice(1)}`
  const normalized = target.startsWith('/') ? target : `/${target}`
  return `/${locale}${normalized}`
}

export function siblingLocalePath(pathname: string, hash: string, next: Locale): string {
  const parts = pathname.split('/').filter(Boolean)
  if (isLocale(parts[0])) {
    parts[0] = next
    return `/${parts.join('/')}${hash}`
  }
  return publicPath(next, pathname === '/' ? '' : pathname) + hash
}

export function useCurrentPublicLocale(): Locale {
  const { locale } = useI18n()
  const location = useLocation()
  return localeFromPath(location.pathname) ?? locale
}

export function usePublicPath(): (target?: string) => string {
  const locale = useCurrentPublicLocale()
  return (target = '') => publicPath(locale, target)
}
